/**
 * The Metadata Studio's editing dock: waveform on the left, typed values on the
 * right, one Save for both.
 *
 * **Edits are drafts until saved, and saved on the way out.** Dragging a marker
 * changes nothing on disk, so a curator can try three Early Outs and keep the
 * last. But stepping to the next track with unsaved work saves it first --
 * moving on is the clearest possible statement that a track is done, and losing
 * a careful cue pass to a keypress would be unforgivable.
 *
 * **One Save, three destinations.** Cue points are saved to Nova's database and
 * mirrored into the file as NOVA_* tags; BPM and key are written to the file
 * through SafeTagWriter; the beat grid stays in Nova's database only. One Save sends each to its own endpoint, cues first, because a refused cue order is the likelier failure and should stop the
 * save before any file is touched.
 *
 * **Shortcuts are captured before the app's own.** The listener runs in the
 * capture phase so Escape can close the shortcut sheet without the app also
 * deselecting the track underneath it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { detectBeatGrid, fetchCues, saveBeatGrid, saveCues, writeTags } from "../api";
import type { BeatGridState, CueField, CueState, Ending, Track } from "../types";
import { lookupKey, toCamelot } from "../format/keys";
import type { KeyNotation } from "../format/notation";
import { MARKERS, MARKER_BY_FIELD, clampField, diffCues, roundMs, type CueValues } from "./cueMath";
import { MetadataForm } from "./MetadataForm";
import { WaveformEditor, type WaveformControls } from "./WaveformEditor";
import { tempoFromTaps, type Grid } from "./beatMath";

const EMPTY: CueValues = { cueIn: null, intro: null, hookStart: null, hookEnd: null, cueOut: null };

function valuesOf(cues: CueState | undefined | null, track: Track): CueValues {
  if (!cues) return { ...EMPTY, cueIn: track.cueIn, cueOut: track.cueOut, intro: track.introEnd };
  return {
    cueIn: cues.cueIn,
    intro: cues.intro,
    hookStart: cues.hookStart,
    hookEnd: cues.hookEnd,
    cueOut: cues.cueOut,
  };
}

/** A person's unsaved grid: new values, or "reset" back to what Nova detected. */
type GridDraft = { bpm: number | null; downbeat: number | null } | "reset" | null;

interface Snapshot {
  gridBase: BeatGridState | null;
  gridDraft: GridDraft;
  track: Track;
  base: CueValues;
  draft: CueValues;
  baseEnding: Ending | null;
  ending: Ending | null;
  bpm: number | null;
  keyCode: string | null;
}

function pending(s: Snapshot) {
  const { changes, clear } = diffCues(s.base, s.draft);
  const cueDirty = Object.keys(changes).length > 0 || clear.length > 0;
  const endingDirty = s.ending !== s.baseEnding;
  return { changes, clear, cueDirty, endingDirty, count:
    Object.keys(changes).length + clear.length + (endingDirty ? 1 : 0) + (s.gridDraft ? 1 : 0) +
    (s.bpm != null ? 1 : 0) + (s.keyCode != null ? 1 : 0) };
}

async function saveSnapshot(
  s: Snapshot
): Promise<{ error?: string; cues?: CueState; tagWarning?: string | null }> {
  const p = pending(s);
  let cues: CueState | undefined;
  let tagWarning: string | null | undefined;
  if (p.cueDirty || p.endingDirty) {
    const res = await saveCues(s.track.id, p.changes, p.clear, p.endingDirty ? s.ending : undefined);
    if (res.error) return { error: res.error };
    cues = res.cues;
    tagWarning = res.tagWarning;
  }
  if (s.gridDraft) {
    const grid =
      s.gridDraft === "reset"
        ? await saveBeatGrid(s.track.id, {}, ["bpm", "downbeat"])
        : await saveBeatGrid(s.track.id, {
            ...(s.gridDraft.bpm != null && s.gridDraft.bpm !== s.gridBase?.bpm ? { bpm: s.gridDraft.bpm } : {}),
            ...(s.gridDraft.downbeat != null && s.gridDraft.downbeat !== s.gridBase?.downbeat
              ? { downbeat: s.gridDraft.downbeat }
              : {}),
          });
    if (grid.error) return { error: `Beat grid not saved: ${grid.error}`, cues };
  }
  if (s.bpm != null || s.keyCode != null) {
    const fields: Record<string, string> = {};
    if (s.bpm != null) fields.bpm = String(s.bpm);
    if (s.keyCode != null) {
      // Camelot, not "Am": the engine's harmony and the playlist arranger read the
      // key tag as a Camelot code, and a musical name there would leave every
      // segue involving this track scored as "unknown".
      const k = lookupKey(s.keyCode);
      if (k) fields.key = k.camelot;
    }
    const written = await writeTags(s.track.id, fields, {});
    if (written.error) return { error: `Cues saved, but the tags were not: ${written.error}`, cues };
  }
  return { cues, tagWarning };
}

const SHORTCUTS: [string, string][] = [
  ["Space", "Play / pause"],
  ["Enter", "Play from the start point"],
  ["Shift+Enter", "Hear the hand-over: 8 s before the mix out"],
  ["H", "Play the highlight"],
  ["I  T  O", "Set start / intro / mix out at the playhead"],
  ["[  ]", "Set highlight start / end at the playhead"],
  ["C  F", "Ending: cold / fade"],
  ["B", "Beat grid: the “1” is at the playhead"],
  [",  .", "Beat grid: nudge 10 ms earlier / later (Shift 1 ms)"],
  ["Y", "Tap the tempo (four taps or more)"],
  ["K", "Click on every beat while playing"],
  ["M", "Select the next marker"],
  ["← →", "Nudge the selected marker 10 ms (Shift 100 ms, Alt 1 ms); seek 1 s if none"],
  ["Delete", "Return the selected marker to what Nova detected"],
  ["= −  0", "Zoom in / out / fit"],
  ["↓ ↑", "Next / previous track (saves first)"],
  ["Ctrl+S", "Save cues, ending, BPM and key"],
  ["?", "This sheet"],
];

export function StudioDock({
  track,
  notation,
  themeKey,
  collapsed,
  onToggleCollapsed,
  onStep,
  onSaved,
  embedded = false,
  controls,
  onSaveShortcut,
  onDirtyChange,
}: {
  track: Track | null;
  notation: KeyNotation;
  themeKey: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Move the selection by one row; returns false at either end. */
  onStep: (delta: 1 | -1) => boolean;
  onSaved: () => void;
  /** Inside the tag editor page: no own header controls; the page's Save covers it. */
  embedded?: boolean;
  /** Filled in each render, so the page can save this section and see if it has changes. */
  controls?: { current: { save: () => Promise<void>; dirty: number } | null };
  /** Ctrl+S here saves the whole page, not only this section. */
  onSaveShortcut?: () => void;
  /** How many unsaved changes this section holds, whenever that changes. */
  onDirtyChange?: (count: number) => void;
}) {
  const waveRef = useRef<WaveformControls | null>(null);
  const [cueState, setCueState] = useState<CueState | null>(null);
  const [draft, setDraft] = useState<CueValues>(EMPTY);
  // Which track the draft belongs to. On the render that switches tracks the
  // draft still holds the previous track's markers; without this tag, saving
  // "the draft" at that moment writes one record's cue points onto another.
  const [draftFor, setDraftFor] = useState<string | null>(null);
  const [ending, setEnding] = useState<Ending | null>(null);
  const [bpm, setBpm] = useState<number | null>(null);
  const [keyCode, setKeyCode] = useState<string | null>(null);
  const [selectedField, setSelectedField] = useState<CueField | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ bad: boolean; text: string } | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [gridBase, setGridBase] = useState<BeatGridState | null>(null);
  const [gridDraft, setGridDraft] = useState<GridDraft>(null);
  const [click, setClick] = useState(false);
  const taps = useRef<number[]>([]);
  const tapTimer = useRef<number | null>(null);

  const base = useMemo(
    () => (track ? valuesOf(cueState ?? track.cues, track) : EMPTY),
    [cueState, track]
  );
  const baseEnding = (cueState ?? track?.cues)?.ending ?? null;
  const savedSources: Partial<CueState["sources"]> = (cueState ?? track?.cues)?.sources ?? {};
  // A marker a person has moved is theirs from that moment, saved or not; drawing
  // it dashed as "detected" until Ctrl+S would misreport whose value it is.
  const sources = Object.fromEntries(
    MARKERS.map(({ field }) => [
      field,
      draft[field] == null ? null : draft[field] !== base[field] ? "set" : savedSources[field] ?? null,
    ])
  ) as Record<CueField, "set" | "detected" | null>;
  const duration = (cueState ?? track?.cues)?.duration ?? track?.duration ?? 0;

  const snapshot: Snapshot | null =
    track && draftFor === track.id
      ? { track, base, draft, baseEnding, ending, bpm, keyCode, gridBase, gridDraft }
      : null;
  // Committed after render, never during it. The switch-track cleanup below runs
  // before this effect's next setup, so it sees the snapshot that was last on
  // screen -- the track being left -- rather than the one being opened.
  const snapRef = useRef(snapshot);
  useEffect(() => {
    snapRef.current = snapshot;
  });
  const dirty = snapshot ? pending(snapshot) : null;

  // Load the track's cue state fresh -- the grid row may be a page old -- and
  // save the previous track's draft on the way out.
  useEffect(() => {
    if (!track) return;
    let cancelled = false;
    const initial = track.cues ?? null;
    setCueState(initial);
    setDraft(valuesOf(initial, track));
    setDraftFor(track.id);
    setEnding(initial?.ending ?? null);
    setBpm(null);
    setKeyCode(null);
    setSelectedField(null);
    setGridBase(null);
    setGridDraft(null);
    taps.current = [];
    void fetchCues(track.id).then((res) => {
      if (cancelled) return;
      setGridBase(res.beatgrid ?? null);
      if (!res.cues) return;
      setCueState(res.cues);
      setDraft(valuesOf(res.cues, track));
      setEnding(res.cues.ending);
    });
    return () => {
      cancelled = true;
      const leaving = snapRef.current;
      snapRef.current = null;
      if (leaving && pending(leaving).count > 0) {
        void saveSnapshot(leaving).then((r) => {
          setMessage(
            r.error
              ? { bad: true, text: `“${leaving.track.title}” was NOT saved: ${r.error}` }
              : { bad: false, text: `Saved “${leaving.track.title}” on the way out.` }
          );
          if (!r.error) onSaved();
        });
      }
    };
    // Keyed on the id: a library refresh replaces the row object, and that must
    // not discard a draft in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track?.id]);

  const save = useCallback(async () => {
    const snap = snapRef.current;
    if (!snap || snap.track.id !== track?.id || pending(snap).count === 0 || saving) return;
    setSaving(true);
    const result = await saveSnapshot(snap);
    setSaving(false);
    if (result.cues) {
      setCueState(result.cues);
      setDraft(valuesOf(result.cues, snap.track));
      setEnding(result.cues.ending);
    }
    if (result.error) {
      setMessage({ bad: true, text: result.error });
      return;
    }
    setBpm(null);
    setKeyCode(null);
    if (snap.gridDraft) {
      setGridDraft(null);
      void fetchCues(snap.track.id).then((res) => res.beatgrid !== undefined && setGridBase(res.beatgrid ?? null));
    }
    setMessage(
      result.tagWarning
        ? { bad: true, text: `Saved in Nova. ${result.tagWarning}` }
        : { bad: false, text: "Saved to Nova and the file." }
    );
    onSaved();
  }, [saving, onSaved, track?.id]);

  const setField = useCallback(
    (field: CueField, t: number | null) => {
      setDraft((d) => ({
        ...d,
        [field]: t == null ? null : roundMs(clampField(field, t, d, duration || Infinity)),
      }));
      setMessage(null);
    },
    [duration]
  );

  const place = useCallback(
    (field: CueField) => {
      const t = waveRef.current?.currentTime() ?? 0;
      setField(field, t);
      setSelectedField(field);
    },
    [setField]
  );

  // The grid in force on screen: the draft over what is saved.
  const gridTempo =
    gridDraft === "reset"
      ? gridBase?.detected?.bpm ?? null
      : gridDraft?.bpm ?? gridBase?.bpm ?? null;
  const gridOne =
    gridDraft === "reset"
      ? gridBase?.detected?.downbeat ?? null
      : gridDraft?.downbeat ?? gridBase?.downbeat ?? null;
  const shownGrid: Grid | null =
    gridTempo && gridOne != null ? { bpm: gridTempo, downbeat: gridOne, beatsPerBar: gridBase?.beatsPerBar ?? 4 } : null;

  const editGrid = useCallback(
    (next: { bpm?: number; downbeat?: number }) => {
      setGridDraft((d) => {
        const from = d === "reset" ? gridBase?.detected : d ?? gridBase;
        const bpmNow = next.bpm ?? (d && d !== "reset" ? d.bpm : null) ?? from?.bpm ?? track?.bpm ?? null;
        const oneNow = next.downbeat ?? (d && d !== "reset" ? d.downbeat : null) ?? from?.downbeat ?? 0;
        return {
          bpm: bpmNow == null ? null : Math.round(bpmNow * 10000) / 10000,
          downbeat: Math.max(0, Math.round(oneNow * 10000) / 10000),
        };
      });
      setMessage(null);
    },
    [gridBase, track?.bpm]
  );

  const oneHere = useCallback(() => {
    if (!gridTempo && !track?.bpm) {
      setMessage({ bad: true, text: "No tempo yet: tap it (Y) first." });
      return;
    }
    editGrid({ downbeat: waveRef.current?.currentTime() ?? 0 });
  }, [editGrid, gridTempo, track?.bpm]);

  const nudgeGrid = useCallback(
    (seconds: number) => gridOne != null && editGrid({ downbeat: gridOne + seconds }),
    [editGrid, gridOne]
  );

  const tap = useCallback(() => {
    if (!track) return;
    taps.current.push(performance.now());
    const rough = tempoFromTaps(taps.current);
    if (rough == null) {
      setMessage({ bad: false, text: `Tap ${Math.max(1, 4 - taps.current.length)} more…` });
      return;
    }
    setMessage({ bad: false, text: `≈ ${rough.toFixed(1)} BPM — finding the beats…` });
    // Ask once the tapping settles, not on every tap.
    if (tapTimer.current) window.clearTimeout(tapTimer.current);
    const id = track.id;
    const keepOne = gridDraft && gridDraft !== "reset" ? gridDraft.downbeat : gridBase?.sources.downbeat === "set" ? gridBase.downbeat : null;
    tapTimer.current = window.setTimeout(() => {
      void detectBeatGrid(id, rough).then((res) => {
        if (snapRef.current?.track.id !== id) return; // moved on to another song
        if (!res.found) {
          setMessage({ bad: true, text: res.error ?? "No beats found near that tempo." });
          return;
        }
        editGrid({ bpm: res.found.bpm, downbeat: keepOne ?? res.found.downbeat });
        // Few beats on the grid means the tap was too far out, or the song has no steady beat.
        setMessage(
          res.found.fit < 0.6
            ? { bad: true, text: `Only ${Math.round(res.found.fit * 100)}% of beats line up at ${res.found.bpm.toFixed(2)} BPM. Tap again, or check with Click.` }
            : { bad: false, text: `Locked to ${res.found.bpm.toFixed(2)} BPM. Press Save to keep it.` }
        );
      });
    }, 700);
  }, [track, gridDraft, gridBase, editGrid]);

  // Studio shortcuts. Capture phase, so they win over the app's global keys.
  useEffect(() => {
    if (!track || collapsed) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        el instanceof HTMLElement &&
        (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
      if (document.querySelector('[role="dialog"]')) return;
      const mod = e.ctrlKey || e.metaKey;
      const wave = waveRef.current;

      if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (onSaveShortcut) onSaveShortcut();
        else void save();
        return;
      }
      if (helpOpen && (e.key === "Escape" || e.key === "?")) {
        e.preventDefault();
        e.stopImmediatePropagation();
        setHelpOpen(false);
        return;
      }
      if (typing || mod) return;

      const handled = (() => {
        const k = e.key;
        const lower = k.toLowerCase();
        if (k === " ") return wave?.togglePlay(), true;
        if (k === "?") return setHelpOpen((o) => !o), true;
        if (k === "Enter" && !e.shiftKey) return wave?.playFrom(draft.cueIn ?? 0), true;
        if (k === "Enter" && e.shiftKey) {
          if (draft.cueOut == null) return false;
          wave?.playFrom(Math.max(0, draft.cueOut - 8));
          return true;
        }
        if (lower === "h") {
          if (draft.hookStart == null) return false;
          wave?.playFrom(draft.hookStart, draft.hookEnd ?? undefined);
          return true;
        }
        const marker = MARKERS.find((m) => m.key.toLowerCase() === lower);
        if (marker) return place(marker.field), true;
        if (lower === "b") return oneHere(), true;
        if (lower === "y") return tap(), true;
        if (lower === "k") return setClick((c) => !c), true;
        if (k === "," || k === "." || k === "<" || k === ">") {
          const sign = k === "," || k === "<" ? -1 : 1;
          nudgeGrid(sign * (e.shiftKey ? 0.001 : 0.01));
          return true;
        }
        if (lower === "c") return setEnding((x) => (x === "cold" ? null : "cold")), true;
        if (lower === "f") return setEnding((x) => (x === "fade" ? null : "fade")), true;
        if (lower === "m") {
          const placed = MARKERS.filter((m) => draft[m.field] != null).map((m) => m.field);
          if (!placed.length) return false;
          const next = placed[(placed.indexOf(selectedField as CueField) + 1) % placed.length];
          setSelectedField(next);
          wave?.reveal(draft[next] as number);
          return true;
        }
        if (k === "ArrowLeft" || k === "ArrowRight") {
          const sign = k === "ArrowLeft" ? -1 : 1;
          if (selectedField && draft[selectedField] != null) {
            const step = e.altKey ? 0.001 : e.shiftKey ? 0.1 : 0.01;
            setField(selectedField, (draft[selectedField] as number) + sign * step);
          } else if (wave) {
            wave.seek(wave.currentTime() + sign * (e.shiftKey ? 5 : 1));
          }
          return true;
        }
        if ((k === "Delete" || k === "Backspace") && selectedField) {
          setField(selectedField, null);
          return true;
        }
        if (k === "=" || k === "+") return wave?.zoomBy(1.5), true;
        if (k === "-" || k === "_") return wave?.zoomBy(1 / 1.5), true;
        if (k === "0") return wave?.zoomFit(), true;
        if (!embedded && (k === "ArrowDown" || k === "ArrowUp")) return onStep(k === "ArrowDown" ? 1 : -1), true;
        return false;
      })();
      if (handled) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [track, collapsed, draft, selectedField, helpOpen, save, place, setField, onStep, embedded, onSaveShortcut,
      oneHere, tap, nudgeGrid]);

  if (controls) controls.current = { save, dirty: dirty?.count ?? 0 };
  const dirtyCount = dirty?.count ?? 0;
  useEffect(() => onDirtyChange?.(dirtyCount), [dirtyCount, onDirtyChange]);

  if (!track) {
    return (
      <section className={`studio-dock empty-dock ${collapsed ? "collapsed" : ""}`}>
        <div className="dock-head">
          <span className="dock-title">Cue editor</span>
          <span className="dock-hint">Select a track to edit its cue points, ending, BPM and key.</span>
          <button className="icon-button dock-toggle" onClick={onToggleCollapsed} title="Collapse">
            {collapsed ? "▴" : "▾"}
          </button>
        </div>
      </section>
    );
  }

  // Verification of the DRAFT, so a badge clears the moment a marker lands.
  const critical = (["cueIn", "cueOut"] as CueField[]).filter((f) => draft[f] == null);
  const advisory = [
    draft.intro == null && "Intro",
    (draft.hookStart == null || draft.hookEnd == null) && "Highlight",
    ending == null && "Ending",
  ].filter(Boolean) as string[];
  const keyInForce = keyCode ?? toCamelot(track.musicalKey);

  return (
    <section className={`studio-dock ${collapsed ? "collapsed" : ""} ${embedded ? "embedded" : ""}`}>
      <div className="dock-head">
        {!embedded && (
          <>
            <button className="icon-button" title="Previous track (↑)" onClick={() => onStep(-1)}>
              ‹
            </button>
            <button className="icon-button" title="Next track (↓)" onClick={() => onStep(1)}>
              ›
            </button>
            <span className="dock-title" title={track.path}>
              {track.title}
              <em> — {track.artist}</em>
            </span>
          </>
        )}
        <span className="badges">
          {critical.map((f) => (
            <span key={f} className="badge critical flash" title="Without it a song may start or end on silence">
              No {MARKER_BY_FIELD[f].label}
            </span>
          ))}
          {advisory.map((a) => (
            <span key={a} className="badge advisory" title="Optional: set it to make the most of this song">
              No {a.toLowerCase()}
            </span>
          ))}
          {critical.length === 0 && advisory.length === 0 && <span className="badge ok">All set</span>}
          <SavedBadge dirty={(dirty?.count ?? 0) > 0} />
        </span>
        <span className="dock-actions">
          {message && <span className={`dock-message ${message.bad ? "bad" : ""}`}>{message.text}</span>}
          <button className="ghost" onClick={() => setHelpOpen((o) => !o)} title="Keyboard shortcuts (?)">
            ⌨ ?
          </button>
          {!collapsed && !embedded && (
            <button
              className="primary"
              disabled={saving || (dirty?.count ?? 0) === 0}
              onClick={() => void save()}
              title="Save cues, ending, BPM and key (Ctrl+S)"
            >
              {saving ? "Saving…" : dirty?.count ? `Save ${dirty.count}` : "Saved"}
              <span className="btn-shortcut">Ctrl+S</span>
            </button>
          )}
          {!embedded && (
            <button className="icon-button dock-toggle" onClick={onToggleCollapsed} title={collapsed ? "Expand" : "Collapse"}>
              {collapsed ? "▴" : "▾"}
            </button>
          )}
        </span>
      </div>

      {!collapsed && (
        <div className="dock-body">
          <div className="dock-wave">
            <WaveformEditor
              ref={waveRef}
              trackId={track.id}
              duration={duration}
              values={draft}
              sources={sources}
              selectedField={selectedField}
              onSelectField={setSelectedField}
              onChange={(field, t) => setField(field, t)}
              themeKey={themeKey}
              onPlayState={setPlaying}
              grid={shownGrid}
              click={click}
            />
            <div className="transport">
              <button className="tool-button play" onClick={() => waveRef.current?.togglePlay()} title="Play / pause (Space)">
                {playing ? "❚❚ Pause" : "▶ Play"}
              </button>
              <button className="tool-button" onClick={() => waveRef.current?.playFrom(draft.cueIn ?? 0)} title="Play from the start point (Enter)">
                ⏮ From start
              </button>
              <button
                className="tool-button"
                disabled={draft.cueOut == null}
                onClick={() => draft.cueOut != null && waveRef.current?.playFrom(Math.max(0, draft.cueOut - 8))}
                title="Hear the last 8 s before the mix out (Shift+Enter)"
              >
                ⇥ Hear the end
              </button>
              <button
                className="tool-button"
                disabled={draft.hookStart == null}
                onClick={() =>
                  draft.hookStart != null && waveRef.current?.playFrom(draft.hookStart, draft.hookEnd ?? undefined)
                }
                title="Play the highlight (H)"
              >
                ♪ Highlight
              </button>
              <span className="transport-sep" />
              <button className="tool-button" onClick={() => waveRef.current?.zoomBy(1 / 1.5)} title="Zoom out (−)">−</button>
              <button className="tool-button" onClick={() => waveRef.current?.zoomFit()} title="Fit (0)">Fit</button>
              <button className="tool-button" onClick={() => waveRef.current?.zoomBy(1.5)} title="Zoom in (=)">＋</button>
              <span className="transport-hint">
                Drag markers · Shift-drag for fine · Ctrl+wheel zoom · click to seek
              </span>
            </div>
            <BeatGridBar
              grid={shownGrid}
              base={gridBase}
              draft={gridDraft}
              click={click}
              onClick={() => setClick((c) => !c)}
              onOne={oneHere}
              onNudge={nudgeGrid}
              onTempo={(n) => editGrid({ bpm: n })}
              onTap={tap}
              onReset={() => {
                setGridDraft(gridBase && (gridBase.sources.bpm === "set" || gridBase.sources.downbeat === "set") ? "reset" : null);
                setMessage(null);
              }}
            />
          </div>
          <div className="dock-form">
            <MetadataForm
              track={track}
              values={draft}
              sources={sources}
              ending={ending}
              selectedField={selectedField}
              notation={notation}
              bpm={bpm ?? track.bpm}
              keyCode={keyInForce}
              onSelectField={(f) => {
                setSelectedField(f);
                if (draft[f] != null) waveRef.current?.reveal(draft[f] as number);
              }}
              onField={setField}
              onPlace={place}
              onEnding={(e) => {
                setEnding(e);
                setMessage(null);
              }}
              onBpm={(n) => setBpm(n)}
              onKey={(k) => setKeyCode(k === toCamelot(track.musicalKey) ? null : k)}
            />
          </div>
        </div>
      )}

      {helpOpen && (
        <div className="shortcut-sheet" role="note">
          <div className="shortcut-head">
            Keyboard <button className="icon-button" onClick={() => setHelpOpen(false)}>×</button>
          </div>
          {SHORTCUTS.map(([keys, what]) => (
            <div key={keys} className="shortcut-row">
              <kbd>{keys}</kbd>
              <span>{what}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Whether the open track has unsaved edits. */
export function SavedBadge({ dirty }: { dirty?: boolean }) {
  return dirty ? (
    <span className="badge sync-local" title="Changed here and not saved yet">Unsaved</span>
  ) : (
    <span className="badge sync-none" title="Saved in Nova and the file">Saved</span>
  );
}

/**
 * The beat grid's row under the transport: the tempo, where the "1" is, and the
 * few moves that fix a grid -- the "1" at the playhead, nudge, half or double,
 * tap -- plus a click to hear it. Changes are drafts until Save, like the cues.
 */
function BeatGridBar({
  grid,
  base,
  draft,
  click,
  onClick,
  onOne,
  onNudge,
  onTempo,
  onTap,
  onReset,
}: {
  grid: Grid | null;
  base: BeatGridState | null;
  draft: GridDraft;
  click: boolean;
  onClick: () => void;
  onOne: () => void;
  onNudge: (seconds: number) => void;
  onTempo: (bpm: number) => void;
  onTap: () => void;
  onReset: () => void;
}) {
  const [typed, setTyped] = useState<string | null>(null);
  const detected = base?.detected;
  const mine = base?.sources.bpm === "set" || base?.sources.downbeat === "set";
  const status = draft
    ? draft === "reset"
      ? "Back to Nova's grid — not saved"
      : "Changed — not saved"
    : mine
      ? "Set by you"
      : detected
        ? [
            `Nova's grid · on the beat ${Math.round(detected.fit * 100)}% of the time`,
            !detected.steady && "the tempo drifts",
            detected.downbeatMargin < 0.5 && "the “1” is a guess",
          ]
            .filter(Boolean)
            .join(" · ")
        : "No beat grid yet. Nova finds one in the background once the song has a BPM — or tap the tempo now.";
  const tempo = grid?.bpm ?? null;
  return (
    <div className="beat-bar" role="group" aria-label="Beat grid">
      <span className="beat-label">Beats</span>
      <input
        className="beat-tempo"
        aria-label="Grid tempo, BPM"
        inputMode="decimal"
        value={typed ?? (tempo != null ? tempo.toFixed(2) : "")}
        placeholder="—"
        onChange={(e) => setTyped(e.target.value)}
        onBlur={() => {
          const n = Number(typed);
          if (typed != null && n >= 30 && n <= 300) onTempo(n);
          setTyped(null);
        }}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      />
      <span className="beat-unit">BPM</span>
      <button className="tool-button" onClick={onOne} title="The “1” of a bar is at the playhead (B)">
        1 here
      </button>
      <button className="tool-button" disabled={!grid} onClick={() => onNudge(-0.01)} title="Move every beat 10 ms earlier (,)">
        ‹ 10 ms
      </button>
      <button className="tool-button" disabled={!grid} onClick={() => onNudge(0.01)} title="Move every beat 10 ms later (.)">
        10 ms ›
      </button>
      <button className="tool-button" disabled={!tempo} onClick={() => tempo && onTempo(tempo / 2)} title="Half the tempo: the grid read every beat twice">
        ½×
      </button>
      <button className="tool-button" disabled={!tempo} onClick={() => tempo && onTempo(tempo * 2)} title="Double the tempo: the grid missed every other beat">
        2×
      </button>
      <button className="tool-button" onClick={onTap} title="Tap along four times or more; Nova locks the grid to the beats near that tempo (Y)">
        Tap
      </button>
      <button
        className={`tool-button ${click ? "on" : ""}`}
        aria-pressed={click}
        disabled={!grid}
        onClick={onClick}
        title="A click on every beat while playing, higher on the “1”, to hear whether the grid is right (K)"
      >
        ♪ Click
      </button>
      {(mine || draft) && (
        <button className="tool-button" onClick={onReset} title="Back to the grid Nova found">
          Reset
        </button>
      )}
      <span className={`beat-status ${draft ? "dirty" : ""}`}>{status}</span>
    </div>
  );
}
